// SVG Flag Icons for language selector and country displays
interface FlagIconProps {
  code: 'tr' | 'gb' | 'en' | 'fr' | 'us' | 'ca' | 'az' | 'cy' | 'ma' | 'mx';
  size?: number;
  className?: string;
}

export function FlagIcon({ code, size = 20, className = '' }: FlagIconProps) {
  const height = Math.round(size * 0.7);

  // Turkey Flag
  if (code === 'tr') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="30" height="20" fill="#E30A17"/>
        <circle cx="11" cy="10" r="6" fill="white"/>
        <circle cx="12.5" cy="10" r="4.8" fill="#E30A17"/>
        <polygon points="16,10 18.5,8.5 17,10.5 19,10.5 17,11.5 18.5,13.5" fill="white"/>
      </svg>
    );
  }

  // UK/GB Flag (Union Jack)
  if (code === 'gb' || code === 'en') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 60 40"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="60" height="40" fill="#012169"/>
        <path d="M0,0 L60,40 M60,0 L0,40" stroke="white" strokeWidth="8"/>
        <path d="M0,0 L60,40" stroke="#C8102E" strokeWidth="4"/>
        <path d="M60,0 L0,40" stroke="#C8102E" strokeWidth="4"/>
        <rect x="25" y="0" width="10" height="40" fill="white"/>
        <rect x="0" y="15" width="60" height="10" fill="white"/>
        <rect x="27" y="0" width="6" height="40" fill="#C8102E"/>
        <rect x="0" y="17" width="60" height="6" fill="#C8102E"/>
      </svg>
    );
  }

  // France Flag
  if (code === 'fr') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="10" height="20" fill="#002395"/>
        <rect x="10" width="10" height="20" fill="white"/>
        <rect x="20" width="10" height="20" fill="#ED2939"/>
      </svg>
    );
  }

  // USA Flag
  if (code === 'us') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        {/* Red and white stripes */}
        <rect width="30" height="20" fill="#B22234"/>
        <rect y="1.54" width="30" height="1.54" fill="white"/>
        <rect y="4.62" width="30" height="1.54" fill="white"/>
        <rect y="7.7" width="30" height="1.54" fill="white"/>
        <rect y="10.78" width="30" height="1.54" fill="white"/>
        <rect y="13.86" width="30" height="1.54" fill="white"/>
        <rect y="16.94" width="30" height="1.54" fill="white"/>
        {/* Blue canton */}
        <rect width="12" height="10.77" fill="#3C3B6E"/>
        {/* Stars (simplified) */}
        <g fill="white">
          <circle cx="2" cy="1.5" r="0.6"/>
          <circle cx="4" cy="1.5" r="0.6"/>
          <circle cx="6" cy="1.5" r="0.6"/>
          <circle cx="8" cy="1.5" r="0.6"/>
          <circle cx="10" cy="1.5" r="0.6"/>
          <circle cx="3" cy="3" r="0.6"/>
          <circle cx="5" cy="3" r="0.6"/>
          <circle cx="7" cy="3" r="0.6"/>
          <circle cx="9" cy="3" r="0.6"/>
          <circle cx="2" cy="4.5" r="0.6"/>
          <circle cx="4" cy="4.5" r="0.6"/>
          <circle cx="6" cy="4.5" r="0.6"/>
          <circle cx="8" cy="4.5" r="0.6"/>
          <circle cx="10" cy="4.5" r="0.6"/>
          <circle cx="3" cy="6" r="0.6"/>
          <circle cx="5" cy="6" r="0.6"/>
          <circle cx="7" cy="6" r="0.6"/>
          <circle cx="9" cy="6" r="0.6"/>
          <circle cx="2" cy="7.5" r="0.6"/>
          <circle cx="4" cy="7.5" r="0.6"/>
          <circle cx="6" cy="7.5" r="0.6"/>
          <circle cx="8" cy="7.5" r="0.6"/>
          <circle cx="10" cy="7.5" r="0.6"/>
          <circle cx="3" cy="9" r="0.6"/>
          <circle cx="5" cy="9" r="0.6"/>
          <circle cx="7" cy="9" r="0.6"/>
          <circle cx="9" cy="9" r="0.6"/>
        </g>
      </svg>
    );
  }

  // Canada Flag
  if (code === 'ca') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="7.5" height="20" fill="#FF0000"/>
        <rect x="7.5" width="15" height="20" fill="white"/>
        <rect x="22.5" width="7.5" height="20" fill="#FF0000"/>
        {/* Maple leaf (simplified) */}
        <path
          d="M15,4 L15.5,6 L17,5.5 L16,7 L18,7 L16.5,8.5 L17.5,9 L15,10 L16,12 L15,11 L15,14 L14,11 L13,12 L14,10 L11.5,9 L12.5,8.5 L11,7 L13,7 L12,5.5 L13.5,6 L14,4 Z"
          fill="#FF0000"
        />
      </svg>
    );
  }

  // Azerbaijan Flag
  if (code === 'az') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="30" height="6.67" fill="#00B5E2"/>
        <rect y="6.67" width="30" height="6.67" fill="#E4002B"/>
        <rect y="13.34" width="30" height="6.67" fill="#00AF66"/>
        {/* Crescent and star */}
        <circle cx="15" cy="10" r="3" fill="white"/>
        <circle cx="16" cy="10" r="2.5" fill="#E4002B"/>
        <polygon points="18.5,10 19.5,9 19,10.2 20,10 19,10.8 19.5,12" fill="white"/>
      </svg>
    );
  }

  // Cyprus (Northern Cyprus) Flag
  if (code === 'cy') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="30" height="20" fill="white"/>
        <rect y="2" width="30" height="1" fill="#E30A17"/>
        <rect y="17" width="30" height="1" fill="#E30A17"/>
        {/* Crescent and star */}
        <circle cx="15" cy="10" r="4" fill="#E30A17"/>
        <circle cx="16.5" cy="10" r="3.2" fill="white"/>
        <polygon points="19,10 20.5,8.5 19.5,10.5 21.5,10.5 19.5,11.5 20.5,13.5" fill="#E30A17"/>
      </svg>
    );
  }

  // Morocco Flag
  if (code === 'ma') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="30" height="20" fill="#C1272D"/>
        {/* Pentagram (Star of Solomon) */}
        <polygon
          points="15,5 16.5,9 21,9 17.5,12 19,16 15,13.5 11,16 12.5,12 9,9 13.5,9"
          fill="none"
          stroke="#006233"
          strokeWidth="1"
        />
      </svg>
    );
  }

  // Mexico Flag
  if (code === 'mx') {
    return (
      <svg
        width={size}
        height={height}
        viewBox="0 0 30 20"
        className={`rounded-sm overflow-hidden ${className}`}
      >
        <rect width="10" height="20" fill="#006847"/>
        <rect x="10" width="10" height="20" fill="white"/>
        <rect x="20" width="10" height="20" fill="#CE1126"/>
        {/* Coat of arms (simplified - eagle) */}
        <circle cx="15" cy="10" r="3" fill="#8B4513"/>
        <ellipse cx="15" cy="10" rx="2" ry="1.5" fill="#006847"/>
      </svg>
    );
  }

  // Fallback - generic globe icon style
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 30 20"
      className={`rounded-sm overflow-hidden ${className}`}
    >
      <rect width="30" height="20" fill="#6B7280"/>
      <circle cx="15" cy="10" r="6" fill="none" stroke="white" strokeWidth="1"/>
      <path d="M15,4 Q12,10 15,16 Q18,10 15,4" fill="none" stroke="white" strokeWidth="1"/>
      <line x1="9" y1="10" x2="21" y2="10" stroke="white" strokeWidth="1"/>
    </svg>
  );
}
